import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardContent, CardActions,
  Chip, IconButton, CircularProgress, Alert, Divider,
  Select, MenuItem, FormControl, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  LinkOff as UnlinkIcon,
  Apps as AppsIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import axiosInstance from '../../utils/axiosConfig';

interface Band {
  _id: string;
  artistName: string;
  mainImage?: string;
}

interface StagePlotSummary {
  _id: string;
  name: string;
  description?: string;
  bandId?: Band | null;
  elements: any[];
  createdAt: string;
  updatedAt: string;
}

const StagePlotsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [plots, setPlots]   = useState<StagePlotSummary[]>([]);
  const [bands, setBands]   = useState<Band[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);

  // Inline band-link state
  const [linkingPlotId, setLinkingPlotId] = useState<string | null>(null);
  const [linkBandValue, setLinkBandValue] = useState<string>('');

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<StagePlotSummary | null>(null);

  // ── Fetch data ─────────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    if (!user?._id) return;
    setLoading(true);
    setError(null);
    try {
      const [plotsRes, bandsRes] = await Promise.all([
        axiosInstance.get<StagePlotSummary[]>('/api/stageplots'),
        axiosInstance.get<Band[]>(`/api/users/${user._id}/bands`),
      ]);
      setPlots(plotsRes.data);
      setBands(bandsRes.data);
    } catch {
      setError('Failed to load your stage plots.');
    } finally {
      setLoading(false);
    }
  }, [user?._id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Actions ────────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axiosInstance.delete(`/api/stageplots/${deleteTarget._id}`);
      setPlots(prev => prev.filter(p => p._id !== deleteTarget._id));
    } catch {
      setError('Failed to delete stage plot.');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleLinkBand = async (plotId: string) => {
    try {
      const res = await axiosInstance.patch<StagePlotSummary>(
        `/api/stageplots/${plotId}/band`,
        { bandId: linkBandValue || null },
      );
      setPlots(prev => prev.map(p => p._id === plotId ? res.data : p));
    } catch {
      setError('Failed to update band link.');
    } finally {
      setLinkingPlotId(null);
      setLinkBandValue('');
    }
  };

  const handleUnlink = async (plotId: string) => {
    try {
      const res = await axiosInstance.patch<StagePlotSummary>(
        `/api/stageplots/${plotId}/band`,
        { bandId: null },
      );
      setPlots(prev => prev.map(p => p._id === plotId ? res.data : p));
    } catch {
      setError('Failed to unlink band.');
    }
  };

  // ── Grouping ───────────────────────────────────────────────────────────────

  const linkedPlots   = plots.filter(p => p.bandId);
  const unlinkedPlots = plots.filter(p => !p.bandId);

  // Group linked plots by band
  const byBand: Record<string, { band: Band; plots: StagePlotSummary[] }> = {};
  linkedPlots.forEach(plot => {
    const band = plot.bandId as Band;
    if (!byBand[band._id]) byBand[band._id] = { band, plots: [] };
    byBand[band._id].plots.push(plot);
  });

  // ── Shared card renderer ───────────────────────────────────────────────────

  const renderPlotCard = (plot: StagePlotSummary) => {
    const isLinking = linkingPlotId === plot._id;

    return (
      <Card
        key={plot._id}
        variant="outlined"
        sx={{ mb: 2, '&:hover': { boxShadow: 3 }, transition: 'box-shadow 0.15s' }}
      >
        <CardContent sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                {plot.name}
              </Typography>
              {plot.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {plot.description}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, ml: 2, flexShrink: 0 }}>
              <Chip
                label={`${plot.elements?.length ?? 0} items`}
                size="small"
                variant="outlined"
                sx={{ fontSize: 11 }}
              />
            </Box>
          </Box>

          <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
            Last updated {new Date(plot.updatedAt).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' })}
          </Typography>

          {/* Inline band linker */}
          {isLinking && (
            <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <Select
                  value={linkBandValue}
                  onChange={e => setLinkBandValue(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value=""><em>No band (unlinked)</em></MenuItem>
                  {bands.map(b => (
                    <MenuItem key={b._id} value={b._id}>{b.artistName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button size="small" variant="contained" onClick={() => handleLinkBand(plot._id)}>Save</Button>
              <Button size="small" onClick={() => { setLinkingPlotId(null); setLinkBandValue(''); }}>Cancel</Button>
            </Box>
          )}
        </CardContent>

        <Divider />

        <CardActions sx={{ px: 1.5, py: 0.75, gap: 0.5 }}>
          <Button
            size="small"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/artist/stage-plots/${plot._id}/edit`)}
          >
            Edit
          </Button>

          {plot.bandId ? (
            <Tooltip title="Unlink from band">
              <Button
                size="small"
                startIcon={<UnlinkIcon />}
                color="inherit"
                sx={{ color: 'text.secondary' }}
                onClick={() => handleUnlink(plot._id)}
              >
                Unlink band
              </Button>
            </Tooltip>
          ) : (
            <Tooltip title="Assign this plot to one of your bands">
              <Button
                size="small"
                startIcon={<LinkIcon />}
                color="inherit"
                sx={{ color: 'text.secondary' }}
                onClick={() => {
                  setLinkingPlotId(plot._id);
                  setLinkBandValue('');
                }}
              >
                Link to band
              </Button>
            </Tooltip>
          )}

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title="Delete stage plot">
            <IconButton size="small" color="error" onClick={() => setDeleteTarget(plot)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </CardActions>
      </Card>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 860, mx: 'auto', px: 3, py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <AppsIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 28 }} />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" fontWeight={700}>Stage Plots</Typography>
          <Typography variant="body2" color="text.secondary">
            {plots.length === 0
              ? 'No stage plots yet — create your first one below.'
              : `${plots.length} plot${plots.length !== 1 ? 's' : ''} across ${Object.keys(byBand).length} band${Object.keys(byBand).length !== 1 ? 's' : ''}`}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/artist/stage-plots/new')}
        >
          New Stage Plot
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {plots.length === 0 ? (
        <Card variant="outlined" sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>You have no stage plots yet</Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
            Create a stage plot to map out your band's setup — instruments, mics, power, monitors and more.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => navigate('/artist/stage-plots/new')}
          >
            Create your first stage plot
          </Button>
        </Card>
      ) : (
        <>
          {/* Plots grouped by band */}
          {Object.values(byBand).map(({ band, plots: bandPlots }) => (
            <Box key={band._id} sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1 }}>
                  {band.artistName}
                </Typography>
                <Chip label={`${bandPlots.length} plot${bandPlots.length !== 1 ? 's' : ''}`} size="small" />
              </Box>
              {bandPlots.map(renderPlotCard)}
            </Box>
          ))}

          {/* Unlinked plots */}
          {unlinkedPlots.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={700} color="text.secondary" sx={{ flexGrow: 1 }}>
                  Not linked to a band
                </Typography>
                <Chip label={`${unlinkedPlots.length}`} size="small" variant="outlined" />
              </Box>
              {unlinkedPlots.map(renderPlotCard)}
            </Box>
          )}
        </>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete stage plot?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StagePlotsListPage;
